const META_SELECTOR = 'meta[name="theme-color"]';

// Colors are managed as a stack: each pushThemeColor adds an entry, and the
// meta tag always reflects the top of the stack (or the document's original
// color once the stack is empty). Popping is order-independent — removing any
// entry re-applies whatever is left on top — so overlapping pushers (a page +
// the nav sidebar + a modal opened from that sidebar) unwind correctly no
// matter which one's cleanup React runs first. The old approach captured the
// literal color present when each push ran and restored it blindly on cleanup,
// which left the tag stuck (usually on a modal's #000000) whenever a parent and
// a nested child unmounted in the same commit.

let baseColor: string | null = null;
const stack: { color: string }[] = [];

function apply(): void {
  const meta = document.querySelector(META_SELECTOR);
  if (!meta) return;
  if (baseColor === null) baseColor = meta.getAttribute('content') ?? '#ffffff';
  const next = stack.length ? stack[stack.length - 1].color : baseColor;
  if (meta.getAttribute('content') !== next) meta.setAttribute('content', next);
}

/**
 * Updates the `theme-color` meta tag — this drives the iOS/mobile browser
 * chrome and status bar tint — to match a page or modal's background.
 * Returns a restore function (use directly as a useEffect cleanup) that
 * removes this entry and re-applies whatever color is now on top, so nested
 * pages/modals stack correctly regardless of teardown order.
 */
export function pushThemeColor(color: string): () => void {
  if (typeof document === 'undefined') return () => {};
  const entry = { color };
  stack.push(entry);
  apply();
  return () => {
    const i = stack.indexOf(entry);
    if (i === -1) return;
    stack.splice(i, 1);
    apply();
  };
}
